import { useState } from 'react';
import { GENRES, REGIONS, SORT_OPTIONS, YEARS } from '@/constants';

export interface CategoryFilters {
  genre: string;
  region: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  value: CategoryFilters;
  onChange: (next: CategoryFilters) => void;
  onReset: () => void;
}

const groups: { key: keyof CategoryFilters; label: string; options: { label: string; value: string }[] }[] = [
  { key: 'genre', label: '类型', options: GENRES.map((g) => ({ label: g, value: g })) },
  { key: 'region', label: '地区', options: REGIONS.map((r) => ({ label: r, value: r })) },
  { key: 'year', label: '年份', options: YEARS.map((y) => ({ label: y, value: y })) },
  { key: 'sort', label: '排序', options: SORT_OPTIONS.map((s) => ({ label: s.label, value: s.key })) },
];

export default function FilterBar({ value, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount = [
    value.genre !== '全部',
    value.region !== '全部',
    value.year !== '全部',
  ].filter(Boolean).length;

  const pick = (key: keyof CategoryFilters, optionValue: string) => {
    onChange({ ...value, [key]: optionValue });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium text-foreground-800 md:hidden"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line" />
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 py-0.5 text-xs text-background-50">{activeCount}</span>
          )}
        </span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`} />
      </button>

      <div className={`${open ? 'block' : 'hidden'} px-4 pb-4 md:block md:p-5`}>
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
              <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">{group.label}</span>
              <div className="flex flex-wrap gap-2">
                {group.options.map((opt) => {
                  const isActive = value[group.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => pick(group.key, opt.value)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
                        isActive
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end border-t border-background-200 pt-4">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}